import type { CommonImageItemType } from '@/components-biz/ImageSelect';
import { mergeHealthCheckIntoContainerDeployConfig } from '@/delivery-type-plugin/capabilities/health-check';
import type { MaasGroupSpec, MaasServiceInfo, MaasSharedFormValues } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { inferUsageScenarioFromLegacyItem, normalizeServiceItem } from '@/delivery-type-plugin/capabilities/service';
/** 根对象解析真源在包级 utils；本文件仅薄适配 / re-export */
import { parseContainerDeployConfigObject } from '@/delivery-type-plugin/utils/parseContainerDeployConfigObject';
import { safeParseJSON } from '@/utils/json-parse';

import {
  type ContainerDeployEnvVarItem,
  type ContainerDeployInstanceScheduling,
  type ContainerDeployInstanceSchedulingGpu,
  isContainerDeployInstanceSchedulingActive,
  resolvePersistentVolumeMountBaseFromContainerDeployConfig,
  resolveTemporaryVolumeMountBaseFromContainerDeployConfig,
} from './containerDeployConfigCore';

export { parseContainerDeployConfigObject };

const CAPABILITY_ALL = 'ALL';

/** 提交 API 时：含 ALL 则只传 ['ALL'] */
function normalizeCapabilitiesForApi(items: string[]): string[] {
  const filtered = items.map((item) => String(item).trim()).filter(Boolean);
  if (!filtered.length) return [];
  if (filtered.includes(CAPABILITY_ALL)) return [CAPABILITY_ALL];
  return filtered;
}

export interface ContainerDeploySystemOverlay {
  security?: Record<string, unknown>;
  devices?: Record<string, unknown>;
  livenessProbe?: Record<string, unknown>;
}

/** 镜像 containerDeployConfig 写入 system.security 的字段 */
export const CONTAINER_DEPLOY_SECURITY_KEYS = ['seccompUnconfined', 'netAdmin', 'privileged', 'capabilities', 'run'] as const;

function stripContainerDeploySecurityKeys(security?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!security || typeof security !== 'object') return undefined;
  const next = { ...security };
  for (const key of CONTAINER_DEPLOY_SECURITY_KEYS) {
    delete next[key];
  }
  return Object.keys(next).length ? next : undefined;
}

/** 制品 containerDeployConfig.healthCheck */
export interface ContainerDeployHealthCheckConfig {
  enabled?: boolean;
  supportedProbeTypes?: string[];
  defaultProbeType?: string;
  defaultPort?: number;
  defaultPath?: string;
  httpMethod?: string;
  defaultCommand?: string;
  initialDelaySeconds?: number;
  periodSeconds?: number;
  timeoutSeconds?: number;
  failureThreshold?: number;
}

/** 镜像 apis → 表单服务项（字段映射与 CI `getServicesFromImageApis` 对齐：usage / protocol / port） */
function getInferenceServicesFromAccessConfig(apis: MaasServiceInfo[]) {
  if (!apis) return undefined;
  if (!Array.isArray(apis)) return undefined;
  return apis.map((s) => {
    const row = s as MaasServiceInfo & {
      path?: string;
      routerPath?: string;
      applicationProtocol?: string;
      /** 镜像预置使用场景（制品字段多为 usage） */
      usage?: string;
      protocol?: string;
      tranProtocol?: string;
      port?: number;
      internalPort?: number;
    };
    const portRaw = row.port ?? row.internalPort;
    const portNum = Number(portRaw);
    const draft = {
      name: String(row.name || '').trim() || undefined,
      usageScenario: String(row.usage ?? row.usageScenario ?? '').trim() || undefined,
      protocol: String(row.tranProtocol || '').trim() || undefined,
      internalPort: Number.isFinite(portNum) && portNum > 0 ? portNum : undefined,
      description: String(row.description || '').trim() || undefined,
      applicationProtocol: String(row.protocol || row.applicationProtocol || '').trim() || undefined,
      routerPath: String(row.path || row.routerPath || '').trim() || undefined,
      _isDefault: true,
    };
    const usageScenario = inferUsageScenarioFromLegacyItem(draft) ?? draft.usageScenario;
    return normalizeServiceItem({ ...draft, usageScenario });
  });
}

export interface ParsedContainerDeployConfig {
  services: ReturnType<typeof getInferenceServicesFromAccessConfig>;
  persistentVolumeMountPath?: string;
  persistentVolumeMountPaths?: string[];
  temporaryVolumeMountPath?: string;
  temporaryVolumeMountPaths?: string[];
  envVars: ContainerDeployEnvVarItem[];
  startupCommands: Record<string, unknown>;
  instanceScheduling?: ContainerDeployInstanceScheduling;
}

const EMPTY_PARSED_CONTAINER_DEPLOY_CONFIG: ParsedContainerDeployConfig = {
  services: [],
  envVars: [],
  startupCommands: {},
};

/** 从 containerDeployConfig 根对象解析 instanceScheduling */
export function normalizeInstanceSchedulingFromContainerDeployConfig(
  config?: Record<string, unknown> | null,
): ContainerDeployInstanceScheduling | undefined {
  return normalizeInstanceScheduling(config ?? undefined);
}

function normalizeInstanceScheduling(config?: Record<string, unknown>): ContainerDeployInstanceScheduling | undefined {
  const raw = config?.instanceScheduling as Record<string, unknown> | undefined;
  if (!raw || typeof raw !== 'object') return undefined;
  const gpus = Array.isArray(raw.gpus)
    ? (raw.gpus as Record<string, unknown>[])
        .map((item) => ({
          resourceType: String(item.resourceType ?? '').trim() || undefined,
          vendor: String(item.vendor ?? '').trim() || undefined,
          device: String(item.device ?? '').trim() || undefined,
          deviceType: String(item.deviceType ?? '').trim() || undefined,
          vendorID: String(item.vendorID ?? '').trim() || undefined,
          deviceID: String(item.deviceID ?? '').trim() || undefined,
          code: String(item.code ?? '').trim() || undefined,
          driverVersion: String(item.driverVersion ?? '').trim() || undefined,
          cudaVersion: String(item.cudaVersion ?? '').trim() || undefined,
        }))
        .filter((item) => item.vendorID || item.deviceID || item.vendor || item.device)
    : undefined;
  const minCpuCores = Number(raw.minCpuCores);
  const minMemoryGi = Number(raw.minMemoryGi);
  const hasConstraints =
    (Number.isFinite(minCpuCores) && minCpuCores > 0) || (Number.isFinite(minMemoryGi) && minMemoryGi > 0) || !!gpus?.length;
  if (!hasConstraints && raw.enabled !== true) return undefined;

  const enabled = raw.enabled === false ? false : raw.enabled === true ? true : hasConstraints;
  const scheduling: ContainerDeployInstanceScheduling = {
    enabled,
    minCpuCores: Number.isFinite(minCpuCores) && minCpuCores > 0 ? minCpuCores : undefined,
    minMemoryGi: Number.isFinite(minMemoryGi) && minMemoryGi > 0 ? minMemoryGi : undefined,
    ...(gpus?.length ? { gpus } : {}),
  };
  return scheduling;
}

/** 解析 containerDeployConfig（JSON 字符串或对象，模型/镜像结构一致） */
export function parseContainerDeployConfigRaw(raw?: string | Record<string, unknown> | null): ParsedContainerDeployConfig {
  const containerDeployConfig = parseContainerDeployConfigObject(raw);
  const { mountPath, mountPaths } = resolvePersistentVolumeMountBaseFromContainerDeployConfig(containerDeployConfig);
  const { mountPath: temporaryMountPath, mountPaths: temporaryMountPaths } =
    resolveTemporaryVolumeMountBaseFromContainerDeployConfig(containerDeployConfig);

  const services = getInferenceServicesFromAccessConfig(
    Array.isArray(containerDeployConfig?.apis) ? (containerDeployConfig.apis as MaasServiceInfo[]) : undefined,
  );
  const envVars = (containerDeployConfig?.envVars || []) as ContainerDeployEnvVarItem[];
  const startupCommands = (containerDeployConfig?.startupCommands || {}) as Record<string, unknown>;
  const instanceScheduling = normalizeInstanceScheduling(containerDeployConfig);

  return {
    services,
    persistentVolumeMountPath: mountPath,
    persistentVolumeMountPaths: mountPaths,
    temporaryVolumeMountPath: temporaryMountPath,
    temporaryVolumeMountPaths: temporaryMountPaths,
    envVars,
    startupCommands,
    instanceScheduling,
  };
}

/** 解析镜像制品上的 containerDeployConfig（JSON 字符串） */
export function parseImageContainerDeployConfig(imageItem?: CommonImageItemType): ParsedContainerDeployConfig {
  if (!imageItem) return EMPTY_PARSED_CONTAINER_DEPLOY_CONFIG;
  return parseContainerDeployConfigRaw(imageItem.containerDeployConfig);
}

/** 序列化 security.capabilities，空数组时不写入；含 ALL 时仅传 ['ALL'] */
function serializeSecurityCapabilities(capabilities?: { drop?: string[]; add?: string[] }) {
  const drop = normalizeCapabilitiesForApi(capabilities?.drop || []);
  const add = normalizeCapabilitiesForApi(capabilities?.add || []);
  if (!drop.length && !add.length) return undefined;
  return {
    ...(drop.length ? { drop } : {}),
    ...(add.length ? { add } : {}),
  };
}

/** 兼容 containerDeployConfig 中 boolean 或 { enabled: boolean } 写法 */
function normalizeContainerDeployFlag(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (value && typeof value === 'object' && 'enabled' in (value as object)) {
    return !!(value as { enabled?: boolean }).enabled;
  }
  return false;
}

/**
 * 将镜像 containerDeployConfig 中的 devices / sharedMemory / security 转为 API system 结构。
 * 与 stack-maas ResourceSystemInfomation（ContainerDevices / ContainerSecurity）一致。
 */
export function buildSystemDevicesAndSecurityFromContainerDeployConfig(
  config?: Record<string, unknown>,
): ContainerDeploySystemOverlay | undefined {
  if (!config) return undefined;

  const devicesCfg = config.devices as Record<string, unknown> | undefined;
  const securityCfg = config.security as Record<string, unknown> | undefined;
  const sharedMemoryCfg = config.sharedMemory as { sizeLimit?: string } | undefined;

  const security: Record<string, unknown> = {};
  if (normalizeContainerDeployFlag(securityCfg?.seccompUnconfined)) {
    security.seccompUnconfined = { enabled: true };
  }
  if (normalizeContainerDeployFlag(securityCfg?.netAdmin)) {
    security.netAdmin = { enabled: true };
  }
  if (normalizeContainerDeployFlag(securityCfg?.privileged)) {
    security.privileged = { enabled: true };
  }
  const capabilities = serializeSecurityCapabilities(securityCfg?.capabilities as { drop?: string[]; add?: string[] } | undefined);
  if (capabilities) {
    security.capabilities = capabilities;
  }

  const runCfg = securityCfg?.run as { runAsUser?: unknown; runAsGroup?: unknown } | undefined;
  const run: Record<string, number> = {};
  const appendRunAs = (key: 'runAsUser' | 'runAsGroup', value: unknown) => {
    const num = Number(value);
    if (Number.isInteger(num) && num >= 0 && num <= 10000) {
      run[key] = num;
    }
  };
  appendRunAs('runAsUser', runCfg?.runAsUser);
  appendRunAs('runAsGroup', runCfg?.runAsGroup);
  if (Object.keys(run).length) {
    security.run = run;
  }

  const devices: Record<string, unknown> = {};
  const virtualDevices = devicesCfg?.virtualDevices as string[] | undefined;
  if (Array.isArray(virtualDevices) && virtualDevices.length) {
    for (const name of virtualDevices) {
      const deviceName = String(name ?? '').trim();
      if (deviceName) {
        devices[deviceName] = {};
      }
    }
  }

  const sharedMemorySize = String(sharedMemoryCfg?.sizeLimit ?? '').trim();
  if (sharedMemorySize) {
    devices.sharedMemory = { size: sharedMemorySize };
  }

  const manualMount = normalizeContainerDeployFlag(devicesCfg?.manualMount);
  const mountItems = devicesCfg?.mounts as { local?: string; mount?: string }[] | undefined;
  if (manualMount && Array.isArray(mountItems) && mountItems.length) {
    const mounts = mountItems
      .map((item) => ({
        hostPath: String(item?.local ?? '').trim(),
        mountPath: String(item?.mount ?? '').trim(),
      }))
      .filter((item) => item.hostPath && item.mountPath);
    if (mounts.length) {
      devices.mounts = mounts;
    }
  }

  const overlay: ContainerDeploySystemOverlay = {};
  if (Object.keys(security).length) overlay.security = security;
  if (Object.keys(devices).length) overlay.devices = devices;
  return Object.keys(overlay).length ? overlay : undefined;
}

function resolveHttpProbeScheme(httpMethod?: string): 'HTTP' | 'HTTPS' {
  const method = String(httpMethod ?? '')
    .trim()
    .toUpperCase();
  if (method === 'HTTPS') return 'HTTPS';
  return 'HTTP';
}

function parseExecProbeCommand(command?: string): string[] | undefined {
  const raw = String(command ?? '').trim();
  if (!raw) return undefined;
  return raw.split(/\s+/).filter(Boolean);
}

/** containerDeployConfig.healthCheck → K8s livenessProbe（enabled 为 false 时不写入） */
export function healthCheckToK8sLivenessProbe(
  healthCheck?: ContainerDeployHealthCheckConfig | Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (!healthCheck || typeof healthCheck !== 'object') return undefined;
  const hc = healthCheck as ContainerDeployHealthCheckConfig;
  if (!hc.enabled) return undefined;

  const probeType = String(hc.defaultProbeType ?? 'http')
    .trim()
    .toLowerCase();
  const probe: Record<string, unknown> = {};

  if (probeType === 'exec') {
    const command = parseExecProbeCommand(hc.defaultCommand);
    if (!command?.length) return undefined;
    probe.exec = { command };
  } else if (probeType === 'tcp') {
    const port = Number(hc.defaultPort);
    if (!Number.isFinite(port) || port <= 0) return undefined;
    probe.tcpSocket = { port };
  } else {
    const port = Number(hc.defaultPort);
    if (!Number.isFinite(port) || port <= 0) return undefined;
    const path = String(hc.defaultPath ?? '').trim() || '/healthz';
    probe.httpGet = {
      path,
      port,
      scheme: resolveHttpProbeScheme(hc.httpMethod),
    };
  }

  const appendTiming = (key: 'initialDelaySeconds' | 'periodSeconds' | 'timeoutSeconds' | 'failureThreshold') => {
    const value = Number(hc[key]);
    if (Number.isFinite(value) && value >= 0) {
      probe[key] = value;
    }
  };
  appendTiming('initialDelaySeconds');
  appendTiming('periodSeconds');
  appendTiming('timeoutSeconds');
  appendTiming('failureThreshold');

  return probe;
}

function buildSystemLivenessProbeFromContainerDeployConfig(
  config?: Record<string, unknown>,
): Pick<ContainerDeploySystemOverlay, 'livenessProbe'> {
  const livenessProbe = healthCheckToK8sLivenessProbe(config?.healthCheck as ContainerDeployHealthCheckConfig | undefined);
  return livenessProbe ? { livenessProbe } : {};
}

/** 将 containerDeployConfig 中的 devices / security / 探活合并为 system overlay */
export function buildContainerDeploySystemOverlay(config?: Record<string, unknown>): ContainerDeploySystemOverlay | undefined {
  const devicesSecurity = buildSystemDevicesAndSecurityFromContainerDeployConfig(config);
  const probes = buildSystemLivenessProbeFromContainerDeployConfig(config);
  const overlay: ContainerDeploySystemOverlay = {
    ...(devicesSecurity || {}),
    ...probes,
  };
  return Object.keys(overlay).length ? overlay : undefined;
}

/** 将 containerDeployConfig 解析结果合并进节点 system */
export function mergeContainerDeployOverlayIntoSystem(
  system: Record<string, unknown> | undefined,
  overlay: ContainerDeploySystemOverlay | undefined,
  options?: { resetContainerDeploySecurity?: boolean },
): Record<string, unknown> | undefined {
  const resetSecurity = options?.resetContainerDeploySecurity;
  if (!overlay && !resetSecurity) return system;

  const next: Record<string, unknown> = { ...(system || {}) };

  if (resetSecurity || overlay?.security) {
    const remaining = stripContainerDeploySecurityKeys(next.security as Record<string, unknown> | undefined);
    const mergedSecurity = { ...(remaining || {}), ...(overlay?.security || {}) };
    if (Object.keys(mergedSecurity).length) {
      next.security = mergedSecurity;
    } else {
      delete next.security;
    }
  }

  if (overlay?.devices) {
    next.devices = { ...((next.devices as Record<string, unknown>) || {}), ...overlay.devices };
  }
  if (overlay) {
    if (overlay.livenessProbe) {
      next.livenessProbe = overlay.livenessProbe;
    } else {
      delete next.livenessProbe;
    }
  }
  return next;
}

/** 按当前选中镜像的 containerDeployConfig 同步推理 system（切换镜像时剔除旧 security 并写入新值） */
export function syncInferenceSystemWithImageContainerDeployConfig(
  system: Record<string, unknown> | undefined,
  imageItem?: CommonImageItemType | null,
): Record<string, unknown> | undefined {
  const config = imageItem?.containerDeployConfig ? parseContainerDeployConfigObject(imageItem.containerDeployConfig) : undefined;
  const overlay = config ? buildContainerDeploySystemOverlay(config) : undefined;
  if (!system && !config) return system;
  return mergeContainerDeployOverlayIntoSystem(system, overlay, { resetContainerDeploySecurity: true });
}

/** 从表单 inferenceFrameworkImage 读取镜像上的 containerDeployConfig，并合并用户编辑的探活配置 */
export function resolveContainerDeployConfigFromFormValues(values: MaasSharedFormValues): Record<string, unknown> | undefined {
  const rawImage = values.inferenceFrameworkImage;
  if (!rawImage) return undefined;
  const imageItem =
    typeof rawImage === 'string'
      ? (safeParseJSON(rawImage, undefined) as CommonImageItemType | undefined)
      : (rawImage as CommonImageItemType);
  if (!imageItem?.containerDeployConfig) return undefined;
  const base = parseContainerDeployConfigObject(imageItem.containerDeployConfig);
  return mergeHealthCheckIntoContainerDeployConfig(base, values as Record<string, unknown>);
}

/** 从 system 中移除 options.containerDeployConfig */
export function stripContainerDeployConfigFromGroupSystem(system?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!system) return system;
  const options = system.options as Record<string, unknown> | undefined;
  if (options?.containerDeployConfig == null) return system;
  const nextOptions = { ...options };
  delete nextOptions.containerDeployConfig;
  const next = { ...system };
  if (Object.keys(nextOptions).length) {
    next.options = nextOptions;
  } else {
    delete next.options;
  }
  return Object.keys(next).length ? next : undefined;
}

/** 从节点组 spec 中移除 system.options.containerDeployConfig */
export function stripContainerDeployConfigFromGroupedSpec(spec: {
  headGroupSpec?: MaasGroupSpec;
  workerGroupSpecs?: MaasGroupSpec[];
  gateway?: MaasGroupSpec;
}): void {
  const stripGroup = (group?: MaasGroupSpec) => {
    if (!group?.system) return;
    const nextSystem = stripContainerDeployConfigFromGroupSystem(group.system as Record<string, unknown>);
    if (nextSystem) {
      group.system = nextSystem;
    } else {
      delete group.system;
    }
  };
  stripGroup(spec.headGroupSpec);
  spec.workerGroupSpecs?.forEach(stripGroup);
  stripGroup(spec.gateway);
}

/** 提交前：将 containerDeployConfig 中的 security/devices/healthCheck 写入各节点组 system，并可选持久化 options.containerDeployConfig */
export function applyContainerDeployConfigToGroupedSpec(
  spec: { headGroupSpec?: MaasGroupSpec; workerGroupSpecs?: MaasGroupSpec[] },
  containerDeployConfig?: Record<string, unknown>,
  options?: { persistInSystemOptions?: boolean },
): void {
  if (!containerDeployConfig) return;

  const persistInSystemOptions = options?.persistInSystemOptions !== false;
  const overlay = buildContainerDeploySystemOverlay(containerDeployConfig);
  const optionsContainerDeployConfig =
    typeof containerDeployConfig === 'string' ? containerDeployConfig : JSON.stringify(containerDeployConfig);

  const applyToGroup = (group?: MaasGroupSpec) => {
    if (!group) return;
    const merged = mergeContainerDeployOverlayIntoSystem(group.system as Record<string, unknown> | undefined, overlay, {
      resetContainerDeploySecurity: true,
    });
    const system = { ...(merged || (group.system as Record<string, unknown>) || {}) };
    const livenessProbe = healthCheckToK8sLivenessProbe(containerDeployConfig?.healthCheck as ContainerDeployHealthCheckConfig | undefined);
    if (livenessProbe) {
      system.livenessProbe = livenessProbe;
    } else {
      delete system.livenessProbe;
    }
    if (persistInSystemOptions) {
      const systemOptions = { ...((system.options as Record<string, unknown>) || {}), containerDeployConfig: optionsContainerDeployConfig };
      system.options = systemOptions;
    }
    group.system = system;
  };

  applyToGroup(spec.headGroupSpec);
  spec.workerGroupSpecs?.forEach(applyToGroup);
}
